import { useRef, useState } from "react";
import { Upload, Link as LinkIcon, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";

export default function ImageUploader({ value, onChange, label = "Image", recommended = "" }) {
  const [mode, setMode] = useState("url"); // 'url' | 'upload'
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-brand-soft">{label}{recommended && <span className="ml-2 text-xs text-brand-soft/50 font-normal">{recommended}</span>}</label>
        <div className="inline-flex bg-brand-ivory rounded-full p-1 text-xs">
          <button type="button" onClick={() => setMode("url")} className={`px-3 py-1 rounded-full ${mode === "url" ? "bg-brand-maroon text-brand-cream" : "text-brand-soft/70"}`}>URL</button>
          <button type="button" onClick={() => setMode("upload")} className={`px-3 py-1 rounded-full ${mode === "upload" ? "bg-brand-maroon text-brand-cream" : "text-brand-soft/70"}`}>Upload</button>
        </div>
      </div>
      {mode === "url" ? (
        <div className="flex gap-2">
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://..."
            className="flex-1 bg-brand-ivory rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40"
          />
          {value && <button type="button" onClick={() => onChange("")} className="p-2 text-brand-soft/50 hover:text-brand-maroon"><X className="w-4 h-4" /></button>}
        </div>
      ) : (
        <div>
          <input ref={fileInput} type="file" accept="image/*" hidden onChange={onPick} />
          <button type="button" onClick={() => fileInput.current?.click()} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-ivory rounded-xl text-sm hover:bg-brand-maroon/10 transition disabled:opacity-60">
            <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Choose image"}
          </button>
          {value && <div className="mt-1 text-xs text-brand-soft/60 break-all flex items-center gap-1"><LinkIcon className="w-3 h-3" /> {value}</div>}
        </div>
      )}
      {value && (
        <div className="mt-2 w-32 h-32 rounded-xl overflow-hidden bg-brand-ivory border border-brand-maroon/10">
          <img src={fileUrl(value)} alt="preview" className="w-full h-full object-cover" />
        </div>
      )}
    </div>
  );
}
